import React, { useState, useEffect, useRef } from 'react';
import { Icons } from '../Icon.jsx';
import PropertySection from './PropertySection.jsx';
import { BREAKPOINTS } from '../../constants.js';

const ResponsivePanel = ({
    primaryNode,
    selectedNodes,
    activeBreakpoint,
    onChange,
    onSelectBreakpoint,
    onViewportWidthChange
}) => {
    const [copyMode, setCopyMode] = useState(false);
    const [sourceBreakpoint, setSourceBreakpoint] = useState(null);
    const [copiedState, setCopiedState] = useState(null);
    const breakpointContainerRef = useRef(null);

    const handleCopyClick = (e) => {
        e.stopPropagation();

        // If already in copy mode, cancel it
        if (copyMode) {
            setCopyMode(false);
            setSourceBreakpoint(null);
            setCopiedState(null);
            return;
        }

        // Capture the current state at the active breakpoint
        const sourceBp = activeBreakpoint;
        let sourceNodeProps = {};
        let sourceComponentOverrides = {};

        if (sourceBp === 'default') {
            // From Default: Get base node props and component props
            const { x, y, width, height, anchors, visibility, responsiveOverrides, ...rest } = primaryNode.props;

            // Copy all standard props (backgroundColor, color, className, etc.)
            sourceNodeProps = { ...rest };

            // Map transform props to layout_ prefixes for overrides
            if (x !== undefined) sourceNodeProps.layout_x = x;
            if (y !== undefined) sourceNodeProps.layout_y = y;
            if (width !== undefined) sourceNodeProps.layout_width = width;
            if (height !== undefined) sourceNodeProps.layout_height = height;
            if (anchors !== undefined) sourceNodeProps.layout_anchors = anchors;

            // Get component props from attachedComponents
            if (primaryNode.attachedComponents) {
                primaryNode.attachedComponents.forEach(comp => {
                    const compProps = comp.props || {};
                    const { responsiveOverrides: compRespOverrides, ...baseCompProps } = compProps;
                    sourceComponentOverrides[comp.id] = baseCompProps;
                });
            }

        } else {
            // From Override: Get override props
            sourceNodeProps = { ...(primaryNode.props.responsiveOverrides?.[sourceBp] || {}) };

            // Get component overrides
            if (primaryNode.attachedComponents) {
                primaryNode.attachedComponents.forEach(comp => {
                    const compOverrides = comp.props?.responsiveOverrides?.[sourceBp];
                    if (compOverrides && Object.keys(compOverrides).length > 0) {
                        sourceComponentOverrides[comp.id] = compOverrides;
                    }
                });
            }
        }

        setCopiedState({
            nodeProps: sourceNodeProps,
            componentOverrides: sourceComponentOverrides
        });
        setCopyMode(true);
        setSourceBreakpoint(sourceBp);
    };

    // Handle click outside to cancel copy mode
    useEffect(() => {
        if (!copyMode) return;

        const handleClickOutside = (e) => {
            if (breakpointContainerRef.current && !breakpointContainerRef.current.contains(e.target)) {
                setCopyMode(false);
                setSourceBreakpoint(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [copyMode]);

    const performCopy = (targetBp) => {
        if (!primaryNode || !copiedState) return;

        // Use the saved copied state
        const sourceNodeProps = copiedState.nodeProps;
        const sourceComponentOverrides = copiedState.componentOverrides;

        // Apply to Target
        if (targetBp === 'default') {
            // To Default: Map layout_ prefixes back to standard props
            const targetNodePatch = {};
            const { layout_x, layout_y, layout_width, layout_height, layout_anchors, ...rest } = sourceNodeProps;

            Object.assign(targetNodePatch, rest);

            if (layout_x !== undefined) targetNodePatch.x = layout_x;
            if (layout_y !== undefined) targetNodePatch.y = layout_y;
            if (layout_width !== undefined) targetNodePatch.width = layout_width;
            if (layout_height !== undefined) targetNodePatch.height = layout_height;
            if (layout_anchors !== undefined) targetNodePatch.anchors = layout_anchors;

            // Apply to base props
            const nodeUpdate = { props: targetNodePatch };

            // Apply component props to base
            if (Object.keys(sourceComponentOverrides).length > 0) {
                const updatedComponents = primaryNode.attachedComponents?.map(comp => {
                    if (sourceComponentOverrides[comp.id]) {
                        return {
                            ...comp,
                            props: {
                                ...comp.props,
                                ...sourceComponentOverrides[comp.id]
                            }
                        };
                    }
                    return comp;
                });
                nodeUpdate.attachedComponents = updatedComponents;
            }

            onChange(nodeUpdate, selectedNodes.map(n => n.id));

        } else {
            // To Override: Set overrides for node and components
            const currentOverrides = primaryNode.props.responsiveOverrides || {};
            const newOverrides = {
                ...currentOverrides,
                [targetBp]: sourceNodeProps
            };

            const nodeUpdate = { props: { responsiveOverrides: newOverrides } };

            // Apply component overrides
            if (Object.keys(sourceComponentOverrides).length > 0) {
                const updatedComponents = primaryNode.attachedComponents?.map(comp => {
                    if (sourceComponentOverrides[comp.id]) {
                        const currentCompOverrides = comp.props.responsiveOverrides || {};
                        return {
                            ...comp,
                            props: {
                                ...comp.props,
                                responsiveOverrides: {
                                    ...currentCompOverrides,
                                    [targetBp]: sourceComponentOverrides[comp.id]
                                }
                            }
                        };
                    }
                    return comp;
                });
                nodeUpdate.attachedComponents = updatedComponents;
            }

            onChange(nodeUpdate, selectedNodes.map(n => n.id));
        }
    };

    const handleBreakpointClick = (bp) => {
        if (copyMode) {
            // Paste the copied state to the clicked breakpoint
            performCopy(bp);
            setCopyMode(false);
            setSourceBreakpoint(null);
            setCopiedState(null);

            // Switch to the editing breakpoint after copying
            if (onSelectBreakpoint) onSelectBreakpoint(bp);

            // Sync viewport width for non-default breakpoints
            if (bp !== 'default' && onViewportWidthChange) {
                const width = BREAKPOINTS[bp];
                onViewportWidthChange(width);
            }
        } else {
            // Normal breakpoint selection
            if (onSelectBreakpoint) onSelectBreakpoint(bp);

            // Only sync viewport for non-default breakpoints
            // Base/default is meant to be edited at any viewport size (viewport stays where it is)
            if (bp !== 'default' && onViewportWidthChange) {
                const width = BREAKPOINTS[bp];
                onViewportWidthChange(width);
            }
        }
    };

    const handleClearClick = (e) => {
        e.stopPropagation();

        // Can't clear default/base breakpoint
        if (activeBreakpoint === 'default') return;

        // Remove overrides for this breakpoint
        const currentOverrides = primaryNode.props.responsiveOverrides || {};
        const { [activeBreakpoint]: removed, ...remainingOverrides } = currentOverrides;

        const nodeUpdate = { props: { responsiveOverrides: remainingOverrides } };

        // Also clear component overrides for this breakpoint
        if (primaryNode.attachedComponents) {
            const updatedComponents = primaryNode.attachedComponents.map(comp => {
                const currentCompOverrides = comp.props.responsiveOverrides || {};
                const { [activeBreakpoint]: removedComp, ...remainingCompOverrides } = currentCompOverrides;

                return {
                    ...comp,
                    props: {
                        ...comp.props,
                        responsiveOverrides: remainingCompOverrides
                    }
                };
            });
            nodeUpdate.attachedComponents = updatedComponents;
        }

        onChange(nodeUpdate, selectedNodes.map(n => n.id));
    };

    const sortedBreakpoints = ['default', 'xs', 'sm', 'md', 'lg', 'xl', '2xl'];

    return (
        <PropertySection
            key={`responsive-${primaryNode.id}`}
            title="Responsive"
            defaultOpen={false}
            action={
                <div className="flex items-center gap-1">
                    <button
                        onClick={handleCopyClick}
                        className={`p-1 rounded hover:bg-slate-200 transition-colors ${copyMode ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:text-gray-800'}`}
                        title="Copy breakpoint properties"
                    >
                        <Icons.Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                        onClick={handleClearClick}
                        disabled={activeBreakpoint === 'default'}
                        className={`p-1 rounded transition-colors ${
                            activeBreakpoint === 'default'
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-600 hover:text-red-500 hover:bg-slate-200'
                        }`}
                        title={activeBreakpoint === 'default' ? "Can't clear base breakpoint" : "Clear breakpoint overrides"}
                    >
                        <Icons.Trash className="w-3.5 h-3.5" />
                    </button>
                </div>
            }
        >
            <div ref={breakpointContainerRef} className="flex flex-col gap-1 bg-slate-50 rounded p-1 border border-slate-200">
                {sortedBreakpoints.map(bp => {
                    const isHidden = primaryNode.props.visibility?.[bp] === false;
                    const isSource = copyMode && bp === sourceBreakpoint;
                    const isSelectable = copyMode && bp !== sourceBreakpoint;
                    const isActive = activeBreakpoint === bp;
                    const hasOverrides = bp !== 'default' && primaryNode.props.responsiveOverrides?.[bp] && Object.keys(primaryNode.props.responsiveOverrides[bp]).length > 0;

                    return (
                        <div 
                            key={bp}
                            className={`flex items-center justify-between p-1 rounded text-xs cursor-pointer transition-colors ${
                                isSource ? 'bg-blue-100 ring-1 ring-blue-300' :
                                isSelectable ? 'hover:bg-green-50 hover:ring-1 hover:ring-green-300' :
                                isActive ? 'bg-white shadow-sm ring-1 ring-blue-400' :
                                'hover:bg-slate-100'
                            }`}
                            onClick={() => handleBreakpointClick(bp)}
                        >
                            <div className="flex items-center gap-2 flex-1">
                                <div className={`w-1.5 h-1.5 rounded-full ${hasOverrides ? 'bg-blue-400' : 'bg-transparent'}`} title={hasOverrides ? 'Has overrides' : ''} />
                                <span className={`font-bold uppercase ${isActive ? 'text-blue-600' : 'text-gray-600'}`}>
                                    {bp === 'default' ? 'Base' : bp}
                                </span>
                                {isActive && <span className="text-[9px] text-blue-400 font-normal ml-1">(Active)</span>}
                                {isSource && <span className="text-[9px] text-blue-500 font-normal ml-auto">Source</span>}
                                {isSelectable && <span className="text-[9px] text-green-600 font-normal ml-auto">Paste here</span>}
                            </div>
                            
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const currentVis = primaryNode.props.visibility || {};
                                    // If currently hidden (false), set to true (visible, which is default so undefined is fine, or explicit true)
                                    // Actually explicit true is better if we want to force visibility, but usually undefined means visible.
                                    // Existing logic: [bp]: isHidden (which is true if it WAS hidden, so we are toggling to visible)
                                    // Wait, isHidden = props.vis[bp] === false.
                                    // If isHidden is true, we want to set it to true (visible)? Or delete the key?
                                    // Existing logic: onChange({ props: { visibility: { ...currentVis, [bp]: isHidden } } })
                                    // If isHidden is true (it is currently hidden), setting [bp]: true makes it visible?
                                    // No, visibility prop: undefined/true = visible, false = hidden.
                                    // If isHidden is true, we want new state to be 'visible'.
                                    // If we set [bp]: true, it is visible.
                                    // If isHidden is false (currently visible), we want new state to be 'hidden' (false).
                                    // So [bp]: !isHidden.
                                    // Wait, the previous code was:
                                    // const isHidden = primaryNode.props.visibility?.[bp] === false;
                                    // onChange(..., [bp]: isHidden)
                                    // If currently hidden (isHidden=true), new value is true (visible). Correct.
                                    // If currently visible (isHidden=false), new value is false (hidden). Correct.
                                    // So yes, we set [bp]: isHidden.
                                    
                                    onChange({ props: { visibility: { ...currentVis, [bp]: isHidden } } }, selectedNodes.map(n => n.id));
                                }}
                                className={`p-1 rounded hover:bg-slate-200 ${isHidden ? 'text-gray-400' : 'text-blue-500'}`}
                                title={`Toggle visibility on ${bp}`}
                            >
                                {isHidden ? <Icons.EyeOff className="w-3.5 h-3.5" /> : <Icons.Eye className="w-3.5 h-3.5" />}
                            </button>
                        </div>
                    );
                })}
            </div>
        </PropertySection>
    );
};

export default ResponsivePanel;
